/** Derives legacy fields from AggregatedSessionV2 for backward-compat with frontend analytics code. */

import type { AggregatedSessionV2, PageSession } from "@global/types/session";

export interface SessionWithDerivedFields extends AggregatedSessionV2 {
  // Derived fields (computed from pageSessions)
  pageFlow: string[];
  pageViews: Record<string, number>;
  timeOnPages: Record<string, number>;
  timeInView: Record<string, number>;
  clickedItem: Record<string, boolean>;
  sessionDuration: number;
}

export function getPageFlow(pageSessions: PageSession[]): string[] {
  return pageSessions.map((ps) => ps.pagePath);
}

export function getPageViews(
  pageSessions: PageSession[]
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const ps of pageSessions) {
    result[ps.pagePath] = (result[ps.pagePath] || 0) + 1;
  }
  return result;
}

/** Derives timeOnPages from pageSessions, in seconds. */
export function getTimeOnPages(
  pageSessions: PageSession[]
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const ps of pageSessions) {
    const timeMs = ps.endTime - ps.startTime;
    const timeSec = timeMs / 1000;
    result[ps.pagePath] = (result[ps.pagePath] || 0) + timeSec;
  }
  return result;
}

/** Derives timeInView from pageSessions events, in seconds. */
export function getTimeInView(
  pageSessions: PageSession[]
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const ps of pageSessions) {
    for (const event of ps.events) {
      if (event.type === "view") {
        const timeMs = event.endTime - event.startTime;
        const timeSec = timeMs / 1000;
        result[event.elementId] = (result[event.elementId] || 0) + timeSec;
      }
    }
  }
  return result;
}

export function getClickedItems(
  pageSessions: PageSession[]
): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const ps of pageSessions) {
    for (const event of ps.events) {
      if (event.type === "interaction" && event.iType === "click") {
        result[event.elementId] = true;
      }
    }
  }
  return result;
}

/** Session duration in seconds, computed from epoch-ms timestamps. */
export function getSessionDuration(
  sessionStart: number,
  sessionEnd: number
): number {
  return (sessionEnd - sessionStart) / 1000;
}

export function adaptSession(
  session: AggregatedSessionV2
): SessionWithDerivedFields {
  const pageSessions = session.pageSessions || [];

  return {
    ...session,
    pageFlow: getPageFlow(pageSessions),
    pageViews: getPageViews(pageSessions),
    timeOnPages: getTimeOnPages(pageSessions),
    timeInView: getTimeInView(pageSessions),
    clickedItem: getClickedItems(pageSessions),
    sessionDuration: getSessionDuration(
      session.sessionStart,
      session.sessionEnd
    ),
  };
}

export function adaptSessions(
  sessions: AggregatedSessionV2[]
): SessionWithDerivedFields[] {
  return sessions.map(adaptSession);
}
